import { useFeatureGate } from "@statsig/react-bindings";
import { useCallback } from "react";
import { useLocalStorage } from "usehooks-ts";
import {
  AppConfig,
  DEV_APP_SETTINGS_KEY,
  getDevAppSettings,
  IS_DEV_APP_SETTINGS_ENABLED_KEY,
  IS_FORCED_VIDEO_PLATFORM_DOWNGRADE_KEY,
} from "../config";
import { enableZoomGate } from "../statsig/gates";
import { Statsig } from "../statsig/StatsigProvider";
import { VideoPlatform } from "../video-sdk";
import { VIDEO_PLATFORMS } from "../video-sdk/core/constants";
import { useIsVideoPlatformDowngraded } from "./useIsVideoPlatformDowngraded";

/**
 * Determines if Zoom video integration is enabled for the current user.
 * This hook implements a provider-dependent feature pattern where patient access
 * to Zoom functionality depends on their provider's Statsig allocation.
 *
 * Uses a feature gate with "providerShortID" (case-sensitive) as the
 * Statsig ID Type, defined as a customID on the StatsigUser.
 * @see gracefullyUpdateStatsigUser for user configuration details
 */
export function useIsZoomEnabled(): [boolean, () => boolean] {
  const [isForcedVideoPlatformDowngrade] = useIsVideoPlatformDowngraded();
  const { value: isZoomGateEnabled } = useFeatureGate(enableZoomGate, {
    disableExposureLog: isForcedVideoPlatformDowngrade,
  });
  const [isDevAppSettingsEnabled] = useLocalStorage<number>(
    IS_DEV_APP_SETTINGS_ENABLED_KEY,
    0,
  );
  const [devAppSettings] = useLocalStorage<Partial<AppConfig>>(
    DEV_APP_SETTINGS_KEY,
    {},
  );

  const hasVideoPlatformOverride =
    isDevAppSettingsEnabled &&
    VIDEO_PLATFORMS.has(devAppSettings?.videoPlatformOverride as VideoPlatform);

  let isZoomEnabled: boolean;
  if (isForcedVideoPlatformDowngrade) {
    // if forced to downgrade, do not enable zoom
    isZoomEnabled = false;
  } else if (hasVideoPlatformOverride) {
    // if video platform override is set, use it
    isZoomEnabled =
      devAppSettings?.videoPlatformOverride === VideoPlatform.Zoom;
  } else {
    // otherwise, use statsig gate
    isZoomEnabled = isZoomGateEnabled;
  }

  const checkZoomIsEnabled = useCallback(() => {
    const devAppSettings = getDevAppSettings();
    const isForcedVideoPlatformDowngrade =
      localStorage.getItem(IS_FORCED_VIDEO_PLATFORM_DOWNGRADE_KEY) === "true";
    const hasVideoPlatformOverride =
      localStorage.getItem(IS_DEV_APP_SETTINGS_ENABLED_KEY) === "1" &&
      VIDEO_PLATFORMS.has(
        devAppSettings?.videoPlatformOverride as VideoPlatform,
      );

    let isZoomEnabled: boolean;
    if (isForcedVideoPlatformDowngrade) {
      // if forced to downgrade, do not enable zoom
      isZoomEnabled = false;
    } else if (hasVideoPlatformOverride) {
      // if video platform override is set, use it
      isZoomEnabled =
        devAppSettings?.videoPlatformOverride === VideoPlatform.Zoom;
    } else {
      // otherwise, use statsig gate
      isZoomEnabled = Statsig.checkGate(enableZoomGate, {
        disableExposureLog: isForcedVideoPlatformDowngrade,
      });
    }

    return isZoomEnabled;
  }, []);

  return [isZoomEnabled, checkZoomIsEnabled];
}
